import XcCallToAction from "@electricMotorShop/Components/XcCallToAction";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps } from "styled-components";

// KK: 2/27/2024 - Added XcCallToAction component

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    userName: state.context.session.userName,
});

const mapDispatchToProps = {
    setSignInOverlayIsOpen,
};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                display: flex;
                flex-direction: row;
                align-items: center;
                width: auto;

                label {
                    min-width: 2.5em;
                }
            `,
            formInputWrapper: css`
                min-width: 4rem;
                margin-top: 0 !important;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            min-height: 2rem;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({ productContext, productSettings, showPrice, showAddToList, userName }) => {
    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper>
            {/* Comment out - Requested by Justin - Opti-203
            {!userName ? <XcCallToAction /> : null}
            */}
            {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withProductContext(ProductListActions)));
